/**
 * Port of `repos/opencode/packages/session-ui/src/components/basic-tool.tsx`, keyed on
 * `styles/components/basic-tool.css` (vendored byte-identical).
 *
 * The collapsible row every tool renders through: a trigger carrying title, subtitle and
 * args, and an expandable body. A tool that is still running cannot be opened — that is
 * upstream's rule, and it is why `pending` gates `onOpenChange` rather than just styling
 * the arrow.
 *
 * Two upstream behaviours are deliberately not ported, neither of which the stylesheet
 * selects on:
 *   - the `motion` spring height animation. The vendored `collapsible.css` already
 *     animates via `--kb-collapsible-content-height`, which `v2-bridge.css` maps onto
 *     Radix's measurement, so the section still expands rather than popping.
 *   - the deferred-mount scheduler, a long-timeline optimisation that trades first paint
 *     for interactivity. Worth revisiting if transcripts get long enough to feel it.
 */

import { useCallback, useEffect, useState } from "react";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";

import { Collapsible } from "./primitives/collapsible.tsx";
import { TextShimmer } from "../ui/text-shimmer.tsx";
import type { IconName } from "../ui/icon.tsx";

export interface TriggerTitle {
  title: string;
  titleClass?: string;
  subtitle?: string;
  subtitleClass?: string;
  args?: string[];
  argsClass?: string;
  action?: ReactNode;
}

function isTriggerTitle(value: unknown): value is TriggerTitle {
  return typeof value === "object" && value !== null && "title" in value;
}

export interface BasicToolProps {
  icon?: IconName;
  trigger: TriggerTitle | ReactNode | ((open: boolean) => ReactNode);
  children?: ReactNode;
  status?: string;
  hideDetails?: boolean;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  forceOpen?: boolean;
  allowOpenWhilePending?: boolean;
  locked?: boolean;
  onSubtitleClick?: () => void;
  onTriggerClick?: (event: MouseEvent<HTMLElement>) => void;
  onTriggerKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  triggerHref?: string;
  triggerAsLink?: boolean;
  clickable?: boolean;
}

export function BasicTool({
  trigger,
  children,
  status,
  hideDetails,
  defaultOpen,
  open: controlledOpen,
  onOpenChange,
  forceOpen,
  allowOpenWhilePending,
  locked,
  onSubtitleClick,
  onTriggerClick,
  onTriggerKeyDown,
  triggerHref,
  triggerAsLink,
  clickable
}: BasicToolProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const open = controlledOpen ?? internalOpen;
  const pending = status === "pending" || status === "running";
  const hasChildren = Boolean(children);

  const setOpen = useCallback(
    (value: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [controlledOpen, onOpenChange]
  );

  useEffect(() => {
    if (!forceOpen || open) return;
    setOpen(true);
  }, [forceOpen, open, setOpen]);

  const handleOpenChange = (value: boolean) => {
    if (pending && !allowOpenWhilePending) return;
    if (locked && !value) return;
    setOpen(value);
  };

  const dynamicTrigger = typeof trigger === "function" ? trigger(open) : undefined;
  const showArrow =
    hasChildren && !hideDetails && !locked && (!pending || allowOpenWhilePending);

  const triggerBody = (
    <div
      data-component="tool-trigger"
      data-clickable={clickable ? "true" : undefined}
      data-hide-details={hideDetails ? "true" : undefined}
    >
      <div data-slot="basic-tool-tool-trigger-content">
        <div data-slot="basic-tool-tool-info">
          {dynamicTrigger !== undefined ? (
            dynamicTrigger
          ) : isTriggerTitle(trigger) ? (
            <div data-slot="basic-tool-tool-info-structured">
              <div data-slot="basic-tool-tool-info-main">
                <span data-slot="basic-tool-tool-title" className={trigger.titleClass}>
                  <TextShimmer text={trigger.title} active={pending} />
                </span>
                {!pending && trigger.subtitle ? (
                  <span
                    data-slot="basic-tool-tool-subtitle"
                    className={[trigger.subtitleClass, onSubtitleClick ? "clickable" : ""]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={(event) => {
                      if (!onSubtitleClick) return;
                      event.stopPropagation();
                      onSubtitleClick();
                    }}
                  >
                    {trigger.subtitle}
                  </span>
                ) : null}
                {!pending && trigger.args?.length
                  ? trigger.args.map((arg) => (
                      <span
                        key={arg}
                        data-slot="basic-tool-tool-arg"
                        className={trigger.argsClass}
                      >
                        {arg}
                      </span>
                    ))
                  : null}
              </div>
              {!pending && trigger.action ? (
                <span data-slot="basic-tool-tool-action">{trigger.action}</span>
              ) : null}
            </div>
          ) : (
            (trigger as ReactNode)
          )}
        </div>
      </div>
      {showArrow ? <Collapsible.Arrow /> : null}
    </div>
  );

  return (
    <Collapsible open={open} onOpenChange={handleOpenChange} className="tool-collapsible">
      {triggerAsLink || triggerHref ? (
        <Collapsible.Trigger asChild>
          <a
            href={triggerHref}
            role={!triggerHref && clickable ? "button" : undefined}
            tabIndex={!triggerHref && clickable ? 0 : undefined}
            data-hide-details={hideDetails ? "true" : undefined}
            onClick={onTriggerClick}
            onKeyDown={onTriggerKeyDown}
          >
            {triggerBody}
          </a>
        </Collapsible.Trigger>
      ) : (
        <Collapsible.Trigger
          data-hide-details={hideDetails ? "true" : undefined}
          onClick={onTriggerClick}
        >
          {triggerBody}
        </Collapsible.Trigger>
      )}
      {hasChildren && !hideDetails ? (
        <Collapsible.Content>{children}</Collapsible.Content>
      ) : null}
    </Collapsible>
  );
}

/** Fields a tool's headline is drawn from, in the order upstream prefers them. */
const LABEL_KEYS = ["description", "query", "url", "filePath", "path", "pattern", "name"];

function label(input: Record<string, unknown> | undefined) {
  return LABEL_KEYS.map((key) => input?.[key]).find(
    (value): value is string => typeof value === "string" && value.length > 0
  );
}

function args(input: Record<string, unknown> | undefined) {
  if (!input) return [];
  const skip = new Set(LABEL_KEYS);
  return Object.entries(input)
    .filter(([key]) => !skip.has(key))
    .flatMap(([key, value]) => {
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return [`${key}=${value}`];
      }
      return [];
    })
    .slice(0, 3);
}

/** The fallback presentation for a tool with no dedicated component. */
export function GenericTool({
  tool,
  status,
  hideDetails,
  input
}: {
  tool: string;
  status?: string;
  hideDetails?: boolean;
  input?: Record<string, unknown>;
}) {
  return (
    <BasicTool
      icon="settings-gear"
      status={status}
      trigger={{
        title: `Called ${tool}`,
        subtitle: label(input),
        args: args(input)
      }}
      hideDetails={hideDetails}
    />
  );
}
